import React from 'react';

import { css } from '@emotion/react';
import { DateOfBirthInputFormatter, LastFourDigits, PhoneNumberInputFormatter } from '@x1/util-format';
import moment from 'moment';

import { PersonalInformationPagePrefillData } from './types';
import Button from 'src/components/creditApplication/Button';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import TextInput from 'src/components/creditApplication/TextInput';
import { useGlobalStore } from 'src/state/global';
import { isValidEmail } from 'src/utils/validations';

interface PropsIface {
  data: PersonalInformationPagePrefillData | null;
}

const PartialPrefillInformationPage = ({ data }: PropsIface) => {
  const { nextPage, nextPageLoading } = React.useContext(NavigationContext);
  const personalInformation = useGlobalStore((s) => s.personalInformation);
  const [firstName, setFirstName] = React.useState<string>(personalInformation.firstName ?? data?.firstName ?? '');
  const [lastName, setLastName] = React.useState<string>(personalInformation.lastName ?? data?.lastName ?? '');
  const [email, setEmail] = React.useState<string>(personalInformation.email ?? data?.email ?? '');
  const [phoneNumber, setPhoneNumber] = React.useState<string>(data?.e164 ?? '');
  const [birthday, setBirthday] = React.useState<string>(data?.dateOfBirth ?? '');
  const firstNameEditable = !personalInformation.firstName && !data?.firstName;
  const lastNameEditable = !personalInformation.lastName && !data?.lastName;
  const emailEditable = !personalInformation.email && !data?.email;
  const phoneNumberEditable = !data?.e164;
  const birthdayEditable = !data?.dateOfBirth;

  const buttonState = React.useMemo(() => {
    const firstNameOk = firstNameEditable ? firstName.length > 0 : true;
    const lastNameOk = lastNameEditable ? lastName.length > 0 : true;
    const emailOk = emailEditable ? email.length > 0 && isValidEmail(email) : true;
    const phoneNumberOk = phoneNumberEditable ? PhoneNumberInputFormatter.deserialize(phoneNumber).length === 10 : true;
    const unformattedBday = DateOfBirthInputFormatter.deserialize(birthday);
    const birthdayOk = birthdayEditable
      ? unformattedBday.length === 8 &&
        moment(unformattedBday, 'MMDDYYYY', true).isValid() &&
        moment(unformattedBday, 'MMDDYYYY').isBefore(moment())
      : true;
    const ok = firstNameOk && lastNameOk && emailOk && phoneNumberOk && birthdayOk;
    return nextPageLoading ? 'loading' : ok ? 'default' : 'disabled';
  }, [
    firstName,
    lastName,
    email,
    phoneNumber,
    birthday,
    firstNameEditable,
    lastNameEditable,
    emailEditable,
    phoneNumberEditable,
    birthdayEditable,
  ]);

  const formattedBirthday = React.useMemo(() => {
    return birthday ? DateOfBirthInputFormatter.serialize(birthday) : '';
  }, [birthday]);

  const onChangeFirstName = React.useCallback(({ value }: { value: string }) => {
    setFirstName(value);
  }, []);

  const onChangeLastName = React.useCallback(({ value }: { value: string }) => {
    setLastName(value);
  }, []);

  const onChangeEmail = React.useCallback(({ value }: { value: string }) => {
    setEmail(value);
  }, []);

  const onChangeBirthday = React.useCallback(({ value }: { value: string }) => {
    const raw = DateOfBirthInputFormatter.serialize(value);
    setBirthday(raw);
  }, []);

  const onChangePhoneNumber = React.useCallback(({ value }: { value: string }) => {
    // Removes all leading 0s and 1s since US e164 shouldn't start with 0 or 1 area codes
    let deserialized = value.replace(/\D/g, '');
    while (deserialized.length > 0 && (deserialized[0] === '0' || deserialized[0] === '1')) {
      deserialized = deserialized.slice(1);
    }

    const formatted = PhoneNumberInputFormatter.serialize(deserialized);
    setPhoneNumber(formatted);
  }, []);

  const onConfirm = React.useCallback(async () => {
    await nextPage();
    const personalInformation = {
      firstName,
      lastName,
      email,
      e164: birthdayEditable ? `+1${PhoneNumberInputFormatter.deserialize(phoneNumber)}` : data?.e164,
      dateOfBirth: birthdayEditable
        ? DateOfBirthInputFormatter.serialize(birthday).replaceAll(' ', '')
        : data?.dateOfBirth,
      ssn: data?.ssnLast4,
    };
    useGlobalStore.setState({ personalInformation });
  }, [
    nextPage,
    firstName,
    lastName,
    email,
    phoneNumber,
    birthday,
    phoneNumberEditable,
    birthdayEditable,
    data?.e164,
    data?.dateOfBirth,
    data?.ssnLast4,
  ]);

  return (
    <CreditApplicationPageLayout title='Your information' subtitle='Enter your information as it appears on your I.D.'>
      <div css={root}>
        <TextInput
          placeholder='First name'
          value={firstName}
          onChange={onChangeFirstName}
          autoComplete='given-name'
          disabled={!firstNameEditable}
        />
        <TextInput
          placeholder='Last name'
          value={lastName}
          onChange={onChangeLastName}
          autoComplete='family-name'
          disabled={!lastNameEditable}
        />
        <TextInput
          placeholder='Email'
          value={email}
          onChange={onChangeEmail}
          autoComplete='email'
          disabled={!emailEditable}
        />
        {!!data?.ssnLast4 && (
          <TextInput value={`••• •• ${LastFourDigits(data.ssnLast4)}`} onChange={() => {}} disabled />
        )}
        <TextInput
          placeholder='Phone number'
          value={phoneNumber}
          onChange={onChangePhoneNumber}
          autoComplete='tel'
          inputMode='numeric'
          disabled={!phoneNumberEditable}
        />
        <TextInput
          placeholder='Birthday (MM/DD/YYYY)'
          value={formattedBirthday}
          onChange={onChangeBirthday}
          autoComplete='bday'
          inputMode='numeric'
          disabled={!birthdayEditable}
        />
      </div>
      <Button
        label='Confirm'
        state={buttonState}
        width='full'
        type='submit'
        onClick={onConfirm}
        overrideRootStyles={{ marginTop: 40 }}
      />
    </CreditApplicationPageLayout>
  );
};

const root = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

export default PartialPrefillInformationPage;
